import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { catchError, throwError } from 'rxjs'
import { environment } from '../../../environments/environment'

/**
 * Generic message for an API failure that carries no backend code — offline /
 * CORS (status 0), throttled, or a gateway/server fault with an HTML or empty
 * body. Without it pages fall back to Angular's English
 * "Http failure response for …" text.
 */
function fallbackKey(status: number): string | null {
  if (status === 0) return 'error.networkError'
  if (status === 429) return 'error.rateLimited'
  if (status >= 500) return 'error.serverError'
  return null
}

function withMessage(error: HttpErrorResponse, body: Record<string, unknown>): HttpErrorResponse {
  return new HttpErrorResponse({
    error: body,
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined,
  })
}

export const errorTranslationInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  const translate = inject(TranslateService)

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!(error instanceof HttpErrorResponse) || error.status === 402) {
        return throwError(() => error)
      }

      const code = error.error?.message
      if (!code || typeof code !== 'string') {
        const key = req.url.startsWith(environment.apiUrl) ? fallbackKey(error.status) : null
        const text = key ? translate.instant(key) : null
        if (typeof text === 'string' && text !== key) {
          const base = error.error && typeof error.error === 'object' && !(error.error instanceof ProgressEvent) ? error.error : {}
          return throwError(() => withMessage(error, { ...base, message: text }))
        }
        return throwError(() => error)
      }

      const translationKey = `error.${code}`
      const translated = translate.instant(translationKey)

      if (translated && translated !== translationKey) {
        return throwError(() => withMessage(error, { ...error.error, message: translated, _originalCode: code }))
      }

      return throwError(() => error)
    }),
  )
}
